import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { User, Edit3, Save, X } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';

export const VitalStatsCard = () => {
  const { profile, updateProfile } = useProfile();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({
    weight_kg: profile?.weight_kg || 0,
    height_cm: profile?.height_cm || 0,
    gender: profile?.gender || '',
    activity_level: profile?.activity_level || 'moderate',
    birth_date: profile?.birth_date || ''
  });

  const calculateAge = (birthDate: string | null) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const calculateBMI = (weight: number | null, height: number | null) => {
    if (!weight || !height || height === 0) return null;
    const heightInMeters = height / 100;
    return (weight / (heightInMeters * heightInMeters)).toFixed(1);
  };

  const handleEdit = () => {
    setEditValues({
      weight_kg: profile?.weight_kg || 0,
      height_cm: profile?.height_cm || 0,
      gender: profile?.gender || '',
      activity_level: profile?.activity_level || 'moderate',
      birth_date: profile?.birth_date || ''
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const { error } = await updateProfile(editValues);
      if (error) throw error;
      
      toast({
        title: "Profile updated",
        description: "Your vital stats have been updated successfully."
      });
      setIsEditing(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update vital stats.",
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const age = calculateAge(profile?.birth_date || null);
  const bmi = calculateBMI(profile?.weight_kg || null, profile?.height_cm || null);

  const activityLevelLabels = {
    sedentary: 'Sedentary (Little or no exercise)',
    light: 'Light (1-3 days/week)',
    moderate: 'Moderate (3-5 days/week)',
    active: 'Active (6-7 days/week)',
    very_active: 'Very Active (Intense daily)'
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 font-normal">
            <User className="h-5 w-5 text-primary" />
            Biometric Data
          </CardTitle>
          {!isEditing ? (
            <Button size="sm" variant="ghost" onClick={handleEdit}>
              <Edit3 className="h-4 w-4" />
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
              <Button size="sm" onClick={handleSave}>
                <Save className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!isEditing ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Weight</span>
              <span className="text-sm font-medium">
                {profile?.weight_kg ? `${profile.weight_kg} kg` : 'Not set'}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Height</span>
              <span className="text-sm font-medium">
                {profile?.height_cm ? `${profile.height_cm} cm` : 'Not set'}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Age</span>
              <span className="text-sm font-medium">
                {age ? `${age} years` : 'Not set'}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Gender</span>
              <span className="text-sm font-medium">
                {profile?.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : 'Not set'}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">BMI</span>
              <span className="text-sm font-medium">
                {bmi || 'N/A'}
              </span>
            </div>

            <div className="border-t pt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Activity Level</span>
                <span className="text-sm font-medium">
                  {profile?.activity_level 
                    ? activityLevelLabels[profile.activity_level as keyof typeof activityLevelLabels]?.split(' ')[0] 
                    : 'Not set'}
                </span>
              </div>
            </div>

            {(!profile?.weight_kg || !profile?.height_cm || !profile?.birth_date || !profile?.gender) && (
              <div className="pt-2 text-xs text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-950/20 p-2 rounded">
                Complete your biometric data for accurate nutrition calculations
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="weight">Weight (kg)</Label>
              <Input
                id="weight"
                type="text"
                inputMode="decimal"
                min="1"
                max="300"
                value={editValues.weight_kg || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(',', '.');
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setEditValues({ ...editValues, weight_kg: parseFloat(value) || 0 });
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="height">Height (cm)</Label>
              <Input
                id="height"
                type="number"
                min="1"
                max="300"
                value={editValues.height_cm || ''}
                onChange={(e) => setEditValues({ ...editValues, height_cm: parseFloat(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="birth_date">Birth Date</Label>
              <Input
                id="birth_date"
                type="date"
                value={editValues.birth_date || ''}
                onChange={(e) => setEditValues({ ...editValues, birth_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select
                value={editValues.gender}
                onValueChange={(value) => setEditValues({ ...editValues, gender: value })}
              >
                <SelectTrigger id="gender">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="activity">Activity Level</Label>
              <Select
                value={editValues.activity_level}
                onValueChange={(value) => setEditValues({ ...editValues, activity_level: value })}
              >
                <SelectTrigger id="activity">
                  <SelectValue placeholder="Select activity level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedentary">Sedentary</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="very_active">Very Active</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
